import { US_STATE_CODES, isUsCountry } from '@constants/usStates.constants';
import { validateEmail } from '@utils/validations';
import { BillingEntityClassification } from 'respona_api/generated/common_pb';

export { isUsCountry };

export const PAYOUT_MAX = {
  contactName: 152,
  businessName: 100,
  email: 200,
  addressLine: 100,
  city: 50,
  stateProvince: 50,
  postalCode: 16,
  tax: 50,
  foreignTin: 50,
  usTin: 30,
} as const;

const PAYOUT_RE = {
  contactName: /^[\p{L} ',\-.]+$/u,
  businessName: /^[\p{L}\p{N} !&'()+,\-./:;]+$/u,
  addressLine: /^[\p{L}\p{N} #'(),\-./:;°]+$/u,
  cityOrState: /^[\p{L} &'()\-.]+$/u,
  postal: /^[A-Za-z0-9 -]{1,16}$/,
  // Permissive across global formats: alphanumerics with spaces / dashes / dots / slashes.
  // Covers e.g. UK NI ("AB 12 34 56 C"), German Steuer-ID, French SIRET, US SSN/EIN.
  taxId: /^[A-Za-z0-9 \-./]{4,}$/,
};

const MIN_AGE_YEARS = 18;
const MAX_AGE_YEARS = 120;

const trimmed = (value: string | undefined | null): string => (value ?? '').trim();

export const isValidContactName = (value: string | undefined): boolean => {
  const v = trimmed(value);
  if (!v || v.length > PAYOUT_MAX.contactName) return false;
  const parts = v.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return false;
  return parts.every((part) => PAYOUT_RE.contactName.test(part));
};

export const isValidBusinessName = (value: string | undefined): boolean => {
  const v = trimmed(value);
  if (!v || v.length > PAYOUT_MAX.businessName) return false;
  return PAYOUT_RE.businessName.test(v);
};

export const isValidAddressLine = (value: string | undefined, required: boolean): boolean => {
  const v = trimmed(value);
  if (!v) return !required;
  if (v.length > PAYOUT_MAX.addressLine) return false;
  return PAYOUT_RE.addressLine.test(v);
};

export const isValidCity = (value: string | undefined): boolean => {
  const v = trimmed(value);
  if (!v || v.length > PAYOUT_MAX.city) return false;
  return PAYOUT_RE.cityOrState.test(v);
};

export const isValidStateProvince = (
  value: string | undefined,
  country: string | undefined
): boolean => {
  const v = trimmed(value);
  if (!v) return false;
  if (isUsCountry(country)) return US_STATE_CODES.has(v.toUpperCase());
  if (v.length > PAYOUT_MAX.stateProvince) return false;
  return PAYOUT_RE.cityOrState.test(v);
};

export const isValidPostalCode = (value: string | undefined): boolean => {
  const v = trimmed(value);
  if (!v) return false;
  return PAYOUT_RE.postal.test(v);
};

export const isValidEmailField = (value: string | undefined): boolean => {
  const v = trimmed(value);
  if (!v || v.length > PAYOUT_MAX.email) return false;
  return validateEmail(v);
};

export const isValidForeignTin = (value: string | undefined): boolean => {
  const v = trimmed(value);
  if (!v || v.length > PAYOUT_MAX.foreignTin) return false;
  return PAYOUT_RE.taxId.test(v);
};

export const isValidUsTin = (value: string | undefined): boolean => {
  const v = trimmed(value);
  if (!v) return true;
  if (v.length > PAYOUT_MAX.usTin) return false;
  return PAYOUT_RE.taxId.test(v);
};

export const isValidDateOfBirth = (timestamp: number | undefined): boolean => {
  if (!timestamp) return false;
  const dob = new Date(timestamp);
  if (Number.isNaN(dob.getTime())) return false;
  const now = new Date();
  if (dob.getTime() > now.getTime()) return false;
  const minBirth = new Date(now.getFullYear() - MIN_AGE_YEARS, now.getMonth(), now.getDate());
  if (dob.getTime() > minBirth.getTime()) return false;
  const maxBirth = new Date(now.getFullYear() - MAX_AGE_YEARS, now.getMonth(), now.getDate());
  if (dob.getTime() < maxBirth.getTime()) return false;
  return true;
};

export type BillingProfileShape = {
  payoutProfileType?: number;
  contactName?: string;
  companyName?: string;
  contactEmail?: string;
  dateOfBirth?: number;
  firstLine?: string;
  secondLine?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  foreignTin?: string;
  usTin?: string;
  countryOfTaxResidence?: string;
  countryOfCitizenship?: string;
  countryOfIncorporation?: string;
  entityClassification?: number;
};

export const validateBillingProfile = (
  form: BillingProfileShape | null | undefined,
  isIndividual: boolean
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!form) {
    errors.form = 'Missing billing details.';
    return errors;
  }

  if (isIndividual) {
    if (!isValidContactName(form.contactName)) {
      errors.contactName = 'Enter your full legal name (first and last).';
    }
    if (!isValidDateOfBirth(form.dateOfBirth)) {
      errors.dateOfBirth = 'Enter a valid date of birth (must be 18 or older).';
    }
  } else if (!isValidBusinessName(form.companyName)) {
    errors.companyName =
      "Company name: max 100 chars; letters, numbers, space and ! & ' ( ) + , - . / : ; only.";
  }

  if (!isValidEmailField(form.contactEmail)) {
    errors.contactEmail = 'Enter a valid email address (max 200 characters).';
  }

  if (!isValidAddressLine(form.firstLine, true)) {
    errors.firstLine =
      "Address line 1: max 100 chars; letters, numbers, space and # ' ( ) , - . / : ; ° only.";
  }
  if (form.secondLine && !isValidAddressLine(form.secondLine, false)) {
    errors.secondLine =
      "Address line 2: max 100 chars; letters, numbers, space and # ' ( ) , - . / : ; ° only.";
  }
  if (!isValidCity(form.city)) {
    errors.city = "City: max 50 chars; letters, space and & ' ( ) - . only.";
  }
  if (!form.country?.trim()) {
    errors.country = 'Select a country.';
  }
  if (!isValidStateProvince(form.state, form.country)) {
    errors.state = isUsCountry(form.country)
      ? 'Select a US state.'
      : "State / Province: max 50 chars; letters, space and & ' ( ) - . only.";
  }
  if (!isValidPostalCode(form.zipCode)) {
    errors.zipCode = 'Postal code: max 16 chars; letters, numbers, space and - only.';
  }

  // ---- Tax form information ----
  // The country of tax residence drives which IRS form applies:
  //   * US tax resident  → W-9 (no citizenship / incorporation / foreign TIN needed)
  //   * non-US individual → W-8BEN  (citizenship + foreign TIN required)
  //   * non-US business   → W-8BEN-E (incorporation + entity classification + foreign TIN required)

  if (!form.countryOfTaxResidence?.trim()) {
    errors.countryOfTaxResidence = 'Select your country of tax residence.';
  }

  const usResident = isUsCountry(form.countryOfTaxResidence);

  if (isIndividual) {
    // Country of citizenship is a W-8BEN field; W-9 doesn't ask for it.
    if (!usResident && !form.countryOfCitizenship?.trim()) {
      errors.countryOfCitizenship = 'Select your country of citizenship.';
    }
  } else {
    // Country of incorporation is a W-8BEN-E field; W-9 doesn't ask for it.
    if (!usResident && !form.countryOfIncorporation?.trim()) {
      errors.countryOfIncorporation = 'Select your country of incorporation.';
    }
    // Entity classification is needed for both W-9 (Box 3) and W-8BEN-E.
    if (
      !form.entityClassification ||
      form.entityClassification === BillingEntityClassification.ENTITY_CLASSIFICATION_UNSPECIFIED
    ) {
      errors.entityClassification = 'Select your entity classification.';
    }
  }

  // TIN requirements split by residence: US residents file W-9 with a US TIN; non-US
  // residents file a W-8 with a foreign TIN (US TIN remains optional in both cases).
  if (usResident) {
    if (!isValidUsTin(form.usTin) || !trimmed(form.usTin)) {
      errors.usTin = isIndividual
        ? 'Enter your US tax ID (SSN or ITIN).'
        : 'Enter your US tax ID (EIN).';
    }
  } else if (!isValidForeignTin(form.foreignTin)) {
    errors.foreignTin =
      'Enter the tax identification number issued by your country of tax residence.';
  }

  if (!usResident && trimmed(form.usTin) && !isValidUsTin(form.usTin)) {
    errors.usTin = 'US tax ID: 4-30 characters; letters, numbers, space, dash, dot and / only.';
  }

  return errors;
};
